// Delivery method types - Updated to match backend expectations
export type DeliveryMethod = "standard" | "express" | "rapido";

export interface DeliveryMethodOption {
  value: DeliveryMethod;
  label: string;
  available: boolean;
  reason?: string; // Why not available
}

export interface ProductLogistics {
  expressDelivery?: boolean;
  limitTime?: boolean;
  daysToDeliver?: number;
  weight?: number;
}

export interface ProductPartner {
  deliveryFeeZone?: {
    documentId: string;
    name?: string;
  };
  deliveryLeadTimeZone?: {
    documentId: string;
    name?: string;
  };
}

export interface DistrictZones {
  deliveryFeeZones?: Array<{
    documentId: string;
    name?: string;
  }>;
  deliveryLeadTimeZones?: Array<{
    documentId: string;
    name?: string;
  }>;
}

/**
 * Calculate available delivery methods for a specific product
 * Based on product logistics and zone compatibility
 */
export const calculateAvailableDeliveryMethods = async (
  product: {
    logistics?: ProductLogistics;
    partner?: ProductPartner;
  },
  districtZones: DistrictZones
): Promise<DeliveryMethodOption[]> => {
  const methods: DeliveryMethodOption[] = [
    {
      value: "standard",
      label: "Entrega Estándar",
      available: false,
    },
    {
      value: "rapido",
      label: "Entrega Rápida",
      available: false,
    },
    {
      value: "express",
      label: "Entrega Express",
      available: false,
    },
  ];

  // Check basic zone presence (simplified - if product has partner zones, assume compatibility)
  // The backend has already validated zone compatibility when calculating delivery fees
  const hasPartnerZones = !!(
    product.partner?.deliveryFeeZone?.documentId &&
    product.partner?.deliveryLeadTimeZone?.documentId
  );

  const hasDistrictZones = !!(
    districtZones.deliveryFeeZones?.length &&
    districtZones.deliveryLeadTimeZones?.length
  );

  if (!hasPartnerZones) {
    // Product doesn't have proper zone configuration
    methods.forEach((method) => {
      method.available = false;
      method.reason = "No hay información de cobertura disponible";
    });
    return methods;
  }

  if (!hasDistrictZones) {
    // User hasn't set their location/address
    methods.forEach((method) => {
      method.available = false;
      method.reason = "Ingresa tu dirección para ver métodos de entrega";
    });
    return methods;
  }

  // Standard Delivery - Always available if zones are compatible
  methods[0].available = true;

  // Rápido Delivery - Available if product supports time limits
  if (product.logistics?.limitTime === true) {
    methods[1].available = true;
  } else {
    methods[1].reason = "Este producto no admite entrega rápida";
  }

  // Express Delivery - Available if product supports it
  if (product.logistics?.expressDelivery === true) {
    methods[2].available = true;
  } else {
    methods[2].reason = "Este producto no admite entrega express";
  }

  return methods;
};

/**
 * Get the delivery method display name in Spanish
 */
export const getDeliveryMethodLabel = (method: DeliveryMethod): string => {
  const labels: Record<DeliveryMethod, string> = {
    standard: "Entrega Estándar",
    rapido: "Entrega Rápida",
    express: "Entrega Express",
  };

  return labels[method] || method;
};

/**
 * Get delivery method icon component
 */
export const getDeliveryMethodIcon = (method: DeliveryMethod) => {
  switch (method) {
    case "express":
      return "Speed";
    case "rapido":
      return "Schedule";
    case "standard":
    default:
      return "LocalShipping";
  }
};

/**
 * Get the current delivery method for a product (with optimistic updates)
 * ARCHITECTURAL FIX: Items don't have delivery methods - they inherit from their group
 */
export const getEffectiveDeliveryMethod = (
  cartCustomId: string,
  groupDeliveryMethod: DeliveryMethod,
  optimisticDeliveryMethods: Record<string, string>
): DeliveryMethod => {
  // Enhanced debugging for delivery method resolution
  console.log(`🔍 [getEffectiveDeliveryMethod] ${cartCustomId}:`, {
    groupDeliveryMethod,
    groupDeliveryMethodType: typeof groupDeliveryMethod,
    optimisticMethod: optimisticDeliveryMethods[cartCustomId],
    optimisticMethodType: typeof optimisticDeliveryMethods[cartCustomId],
    hasOptimistic: !!optimisticDeliveryMethods[cartCustomId],
  });

  // Check if there's an optimistic update for this product
  const optimisticMethod = optimisticDeliveryMethods[cartCustomId];
  if (optimisticMethod) {
    console.log(
      `✅ [getEffectiveDeliveryMethod] Using optimistic method: ${optimisticMethod}`
    );
    return optimisticMethod as DeliveryMethod;
  }

  // ARCHITECTURAL FIX: Use the group's delivery method as the source of truth
  const effectiveMethod = groupDeliveryMethod || "standard";
  console.log(
    `✅ [getEffectiveDeliveryMethod] Using group method: ${effectiveMethod}`
  );
  return effectiveMethod;
};

/**
 * Check if a delivery method change would require group reorganization
 */
export const requiresGroupReorganization = (
  currentGroupItems: Array<{
    cartCustomId: string;
    deliveryMethod?: DeliveryMethod;
  }>,
  changingItemId: string,
  newDeliveryMethod: DeliveryMethod
): boolean => {
  // If there are other items in the group with different delivery methods,
  // reorganization will be needed
  const otherItems = currentGroupItems.filter(
    (item) => item.cartCustomId !== changingItemId
  );

  if (otherItems.length === 0) {
    // No other items, no reorganization needed
    return false;
  }

  // Check if any other item has a different delivery method
  return otherItems.some((item) => {
    const itemMethod = item.deliveryMethod || "standard";
    return itemMethod !== newDeliveryMethod;
  });
};
